import {useEffect, useRef, useState} from 'react';
import {Link, useSearchParams} from 'react-router-dom';
import {usePlaces} from '../api/places';
import {useMemories} from '../api/plants';
import {isUnfiltered, useFeed, usePlants} from '../api/posts';
import FilterSheet from '../components/tell/FilterSheet';
import EmptyState from '../components/feedback/EmptyState';
import ErrorBox from '../components/feedback/ErrorBox';
import {SkeletonList} from '../components/feedback/Skeleton';
import Spinner from '../components/feedback/Spinner';
import Icon from '../components/Icon';
import KindChips from '../components/tell/KindChips';
import MemoryCard from '../components/tell/MemoryCard';
import DueBand from '../components/todo/DueBand';
import TellCard from '../components/tell/TellCard';
import TellRow from '../components/tell/TellRow';
import {useMe} from '../hooks/useAuth';
import {groupByDay} from '../lib/groupByDay';
import {KINDS, kindInfo} from '../lib/kinds';
import {pickMemory} from '../lib/memories';
import {dateKicker} from '../lib/season';
import {todayLocal} from '../lib/when';
import {FeedFilters, Kind} from '../types';

// Home (design/screens/Main.dc.html): date kicker, "Journal", kind chips, then the feed grouped
// by day with quiet headers. The Due band (Phase 3) and the Last-year card have their slots
// above the feed and render only when they have something to say. Filters live in the
// URL so Back works; a plant/place/date filter switches to compact rows for scanning.

const isKind = (v: string | null): v is Kind => KINDS.some(k => k.kind === v);

export function useFeedFilters(): [FeedFilters, (next: FeedFilters) => void] {
    const [params, setParams] = useSearchParams();
    const kind = params.get('kind');
    const filters: FeedFilters = {
        kind: isKind(kind) ? kind : null,
        detail: params.get('detail'),
        plantId: params.get('plantId'),
        species: params.get('species'),
        placeId: params.get('placeId'),
        happy: params.get('happy') === '1' ? '1' : null,
        from: params.get('from'),
        to: params.get('to')
    };
    const set = (next: FeedFilters) => {
        const p = new URLSearchParams();
        for (const [k, v] of Object.entries(next)) if (v) p.set(k, v);
        setParams(p, {replace: true});
    };
    return [filters, set];
}

export default function JournalPage() {
    const me = useMe();
    const [filters, setFilters] = useFeedFilters();
    const feed = useFeed(filters);
    const sentinel = useRef<HTMLDivElement>(null);
    const compact = !!(filters.plantId || filters.species || filters.placeId || filters.from || filters.to);
    const [sheetOpen, setSheetOpen] = useState(false);
    const plants = usePlants();
    const places = usePlaces(true);
    const unfiltered = isUnfiltered(filters);
    const memories = useMemories(7);
    const memory = unfiltered && memories.data ? pickMemory(memories.data.items, memories.data.today) : null;

    const summary = [
        filters.happy ? 'Happy' : null,
        filters.kind ? kindInfo(filters.kind).label : null,
        filters.detail,
        filters.plantId ? (plants.data?.find(p => p.id === filters.plantId)?.label ?? 'plant') : null,
        filters.species ? (plants.data?.find(p => p.name.toLowerCase() === filters.species)?.name ?? filters.species) : null,
        filters.placeId ? (places.data?.find(p => p.id === filters.placeId)?.name ?? 'place') : null,
        filters.from || filters.to ? `${filters.from ?? '…'} to ${filters.to ?? '…'}` : null
    ].filter(Boolean);

    // Load the next page as the end of the list scrolls into view.
    useEffect(() => {
        const node = sentinel.current;
        if (!node || !feed.hasNextPage) return;
        const observer = new IntersectionObserver(entries => {
            if (entries.some(e => e.isIntersecting) && !feed.isFetchingNextPage) void feed.fetchNextPage();
        });
        observer.observe(node);
        return () => observer.disconnect();
    }, [feed.hasNextPage, feed.isFetchingNextPage, feed.fetchNextPage, feed]);

    const posts = feed.data?.pages.flatMap(p => p.items) ?? [];
    const groups = groupByDay(posts, todayLocal());
    const name = me.data?.firstName;
    const filtered = filters.kind !== null || !!filters.happy || compact;

    return (
        <div className="settle-in flex flex-col gap-5">
            <header className="flex items-end justify-between pt-2">
                <div>
                    <div className="meta mb-1">{dateKicker(new Date(), me.data?.hemisphere)}</div>
                    <h1 className="display m-0">Journal</h1>
                </div>
                <button
                    type="button"
                    aria-label="Filter"
                    aria-pressed={compact}
                    className={`flex h-11 w-11 items-center justify-center rounded-pill border ${compact ? 'border-shoot bg-shoot-soft text-shoot-text' : 'border-line bg-surface text-ink-2'}`}
                    onClick={() => setSheetOpen(true)}
                >
                    <Icon name="filter" size={20} />
                </button>
            </header>

            <KindChips
                value={filters.kind ?? null}
                onChange={kind => setFilters({...filters, kind, detail: null})}
                onAll={() => setFilters({...filters, kind: null, detail: null, happy: null})}
                happy={!!filters.happy}
                onHappy={happy => setFilters({...filters, happy: happy ? '1' : null})}
            />

            {filtered && feed.isSuccess && (
                <div className="meta -mt-2 flex items-center gap-2">
                    <span className="min-w-0 flex-1 truncate">
                        {posts.length}
                        {feed.hasNextPage ? '+' : ''} {posts.length === 1 && !feed.hasNextPage ? 'tell' : 'tells'} · {summary.join(' · ')}
                    </span>
                    <button type="button" className="shrink-0 font-semibold text-shoot-text underline-offset-2 hover:underline" onClick={() => setFilters({})}>
                        Clear
                    </button>
                </div>
            )}

            {sheetOpen && <FilterSheet open onClose={() => setSheetOpen(false)} filters={filters} onApply={setFilters} />}

            {unfiltered && <DueBand />}

            {memory && memories.data && <MemoryCard post={memory} today={memories.data.today} />}

            {feed.isPending && <SkeletonList rows={2} kind="card" />}
            {feed.isError && <ErrorBox error={feed.error} onRetry={() => void feed.refetch()} />}

            {feed.isSuccess && posts.length === 0 && (
                filtered ? (
                    <EmptyState icon={filters.kind ? kindInfo(filters.kind).icon : 'filter'} tone="plain" title="Nothing matches that yet.">
                        <button type="button" className="font-semibold text-shoot-text underline-offset-2 hover:underline" onClick={() => setFilters({})}>
                            Show everything
                        </button>
                    </EmptyState>
                ) : (
                    <EmptyState icon="journal" title={`Nothing here yet${name ? `, ${name}` : ''}.`}>
                        Tap the camera, snap what&rsquo;s in front of you, say what it is. Ten seconds, gloves on.
                    </EmptyState>
                )
            )}

            {groups.map(group => (
                <section key={group.day} className="flex flex-col gap-3">
                    <h2 className="m-0 font-display text-lg font-bold">{group.label}</h2>
                    {compact ? (
                        <div className="rows card px-4">
                            {group.posts.map(post => (
                                <TellRow key={post.id} post={post} to={`/tell/${post.id}`} />
                            ))}
                        </div>
                    ) : (
                        group.posts.map(post => <TellCard key={post.id} post={post} />)
                    )}
                </section>
            ))}

            <div ref={sentinel} className="flex justify-center py-2">
                {feed.isFetchingNextPage && <Spinner label="Loading older tells" />}
                {!feed.hasNextPage && posts.length > 0 && <span className="meta">That&rsquo;s everything.</span>}
            </div>

            <Link
                to="/tell/new"
                aria-label="New tell"
                className="fixed bottom-[104px] right-4 z-10 flex h-16 w-16 items-center justify-center rounded-fab bg-shoot text-on-shoot shadow-float no-underline active:scale-95"
            >
                <Icon name="camera" size={30} />
            </Link>
        </div>
    );
}
